"use client";

import {
  createContext,
  useContext,
  useMemo,
} from "react";

import { StudioMemberRole } from "@prisma/client";

import { FullMember, MemberWithUserAndProfile } from "@/lib/types";

type MemberPermissions = {
  isOwnerOrAdmin: boolean;
  isOwnerOrAdminOrCoach: boolean;
  isAdmin: boolean;
  isOwner: boolean;
  isActiveMember: boolean;
  canViewPage: boolean;
  canViewPost: boolean;
};

interface MemberContext {
  currentMember: FullMember | null;
  permissions: MemberPermissions;
}

export const MemberContext = createContext<MemberContext | undefined>(
  undefined,
);

export function MemberProvider({
  children,
  currentMember = null,
}: {
  children: React.ReactNode;
  currentMember?: MemberWithUserAndProfile | null;
}) {
  const permissions = useMemo((): MemberPermissions => ({
    isOwner: currentMember?.role === StudioMemberRole.OWNER,
    isAdmin: currentMember?.role === StudioMemberRole.ADMIN,
    isOwnerOrAdminOrCoach:
      currentMember?.role === StudioMemberRole.OWNER ||
      currentMember?.role === StudioMemberRole.ADMIN ||
      currentMember?.role === StudioMemberRole.COACH,
    isOwnerOrAdmin:
      currentMember?.role === StudioMemberRole.OWNER ||
      currentMember?.role === StudioMemberRole.ADMIN,
    isActiveMember: currentMember?.role === StudioMemberRole.MEMBER,
    canViewPage: true,
    canViewPost: true,
  }), [currentMember?.role]);

  const contextValue = useMemo(() => ({
    currentMember: currentMember as FullMember,
    permissions,
  }), [currentMember, permissions]);

  return (
    <MemberContext.Provider value={contextValue}>
      {children}
    </MemberContext.Provider>
  );
}

// export const MemberPermissionCheck = ({
//   roles,
//   children
// }: {
//   roles: StudioMemberRole[];
//   children: ReactNode;
// }) => {
//   return {children}
// }

export function useMember() {
  // Hooks must be called unconditionally at the top level
  const context = useContext(MemberContext);
  
  // Return default if context is not available
  if (context === undefined) {
    return {
      currentMember: null,
      permissions: {
        isOwnerOrAdmin: false,
        isOwnerOrAdminOrCoach: false,
        isAdmin: false,
        isOwner: false,
        isActiveMember: false,
        canViewPage: false,
        canViewPost: false,
      },
    };
  }
  
  return context;
}
